import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { ActionBar } from '~/components/ui/action-bar';
import { InlineMarkdown } from '~/components/inline-markdown';
import { cn } from '~/lib/utils';
import { PickerDialogShell } from './picker-dialog-shell';
import {
  filterOfferableTools,
  normalizeToolSearch,
  toolAccessKeys,
  type StaleAppTool,
  type ToolAccessResponse,
} from './tool-access-keys';
import {
  buildPutPayload,
  buildSavePayload,
  groupOfferableTools,
  toolDisplayName,
  nextDraft,
  selectAllVisible,
  deselectAllVisible,
} from './tool-picker-model';

const STALE_REASON_LABEL: Record<StaleAppTool['reason'], string> = {
  unknown: 'No longer exists',
  not_offerable: 'No longer offered to apps',
};

// Wire save-error codes translated into owner-actionable text.
const TOOL_ACCESS_SAVE_ERROR_MESSAGES: Record<string, string> = {
  too_many_tools: 'That is more tools than one app can have — untick a few and save again.',
  not_offerable:
    'One or more of those tools can no longer be offered to apps — refresh and try again.',
  tool_access_save_failed: 'Something went wrong while saving — try again.',
};

function describeToolAccessSaveError(e: Error): string {
  return TOOL_ACCESS_SAVE_ERROR_MESSAGES[e.message] ?? e.message;
}

export interface ToolPickerDialogProps {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The owner-facing picker for FRONT-1385's per-app native-tool allowlist.
 *
 * Originally embedded inline in the "Eddy tools" capability card; moved into
 * this dialog after product testing — a picker squeezed into a card read
 * cramped once the list got long, and a dedicated "Select Tools" button
 * (`capabilities-section.tsx`) opening a roomy modal is the requested shape.
 * Groups the flat list into named sections and filters by word (`tool-picker-model.ts`).
 *
 * This is the ONLY mounted copy of this list in the app editor — Radix's
 * `Dialog` only renders `DialogContent` into the DOM while `open` is true,
 * so `CapabilitiesSection` no longer keeps an inline instance around next to
 * this one (a duplicate mount previously produced colliding DOM ids for the
 * same tool's checkbox).
 *
 * D6 (backend, `tool-offer.ts`): a stored allowlist entry that has fallen out
 * of the live offerable set is never auto-pruned — it's reported. Those rows
 * render first, checked and disabled (an individual checkbox can't remove
 * them — only the bulk "Remove them" action can, which PUTs the pruned list
 * straight away rather than waiting on the draft's own Save).
 */
export function ToolPickerDialog({ slug, open, onOpenChange }: ToolPickerDialogProps) {
  const qc = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // No debounce: a debounce is what previously let "Select all" race a stale filtered view.
  const [search, setSearch] = useState('');

  // This component itself stays mounted the whole time the app editor is
  // open (only the Dialog's own visible content unmounts/remounts with
  // `open`) — so its hooks don't reset for free the way a genuinely
  // unmounted component's would. Clear the search box on every closed ->
  // open edge so a term typed on a previous visit never silently filters
  // the list the next time this opens.
  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  // Shares its cache entry with `CapabilitiesSection`'s own query — one list
  // per app (no search-scoped variant, see tool-access-keys.ts) — so opening
  // this dialog doesn't refetch when the card's own query already has the
  // data warm.
  const { data, isLoading, isError } = useQuery({
    queryKey: toolAccessKeys.list(slug),
    queryFn: () => api.get<ToolAccessResponse>(`/api/admin/apps/${slug}/tool-access`),
    staleTime: 30_000,
  });

  const tools = data?.tools ?? [];
  const selected = data?.selected ?? [];
  const stale = data?.stale ?? [];
  const staleNames = useMemo(() => new Set(stale.map((s) => s.name)), [stale]);
  // Dirty/save-payload decisions filter through this, not `staleNames`.
  const offerableNames = useMemo(() => new Set(tools.map((t) => t.name)), [tools]);

  const normalizedSearch = normalizeToolSearch(search);
  const filteredTools = useMemo(() => filterOfferableTools(tools, search), [tools, search]);
  const groups = useMemo(() => groupOfferableTools(filteredTools), [filteredTools]);
  const visibleNames = useMemo(() => filteredTools.map((t) => t.name), [filteredTools]);

  const save = useMutation({
    mutationFn: (toolNames: string[]) =>
      api.put<{ ok: true; toolNames: string[] }>(`/api/admin/apps/${slug}/tool-access`, {
        toolNames,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: toolAccessKeys.lists(slug) });
      toast.success('Tool access saved');
    },
    onError: (e: Error) => toast.error(`Save failed: ${describeToolAccessSaveError(e)}`),
  });

  const [draft, setDraft] = useState<string[] | null>(null);
  const current = draft ?? selected;
  useEffect(() => {
    // Only clear an in-progress draft once it has actually been PERSISTED —
    // i.e. the payload this draft would have sent now matches what the
    // server just returned (the shape right after this draft's own Save
    // completes and the list refetches). A plain `if (data) setDraft(null)`
    // fired on ANY refetch — window refocus, the 30s staleTime lapsing —
    // and silently discarded the owner's unsaved ticks (FRONT-1385 review
    // finding 11). Compares through the stable `offerableNames`, so this
    // still clears correctly right after a save that pruned a stale entry.
    if (draft !== null && nextDraft(draft, selected, offerableNames) === null) {
      setDraft(null);
    }
    // `draft` is intentionally excluded — this effect reacts to fresh
    // SERVER data, never to the owner's own local edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, offerableNames]);

  function toggle(name: string) {
    // Stale rows aren't individually toggleable — "Remove them" is the only
    // way to clear one (D6: never silently pruned by an ordinary uncheck).
    if (staleNames.has(name)) return;
    setDraft((prev) => {
      const base = prev ?? selected;
      const candidate = base.includes(name) ? base.filter((x) => x !== name) : [...base, name];
      return nextDraft(candidate, selected, offerableNames);
    });
  }

  // Act on the visible (filtered) tools only; always enabled via `nextDraft`.
  function selectAll() {
    setDraft((prev) => nextDraft(selectAllVisible(prev ?? selected, visibleNames), selected, offerableNames));
  }
  function deselectAll() {
    setDraft((prev) => nextDraft(deselectAllVisible(prev ?? selected, visibleNames), selected, offerableNames));
  }

  // `nextDraft` already returns `null` for a non-dirty candidate.
  const dirty = draft !== null;

  function removeStale() {
    save.mutate(buildSavePayload(selected, offerableNames));
  }

  // Same basis the toolbar's own count reads, so the two can't disagree.
  const selectedOfferableCount = buildSavePayload(current, offerableNames).length;

  return (
    <PickerDialogShell
      open={open}
      onOpenChange={onOpenChange}
      eyebrow="app capabilities"
      title="Select tools"
      initialFocusRef={searchInputRef}
      description={
        <>
          Pick which of your EDDY tools this app may call via{' '}
          <code className="rounded bg-muted px-1 py-0.5 text-foreground">eddy.tools.*</code>.
          Anyone who opens the published app can run a ticked tool — as you, on your compute
          budget — so pick only what the app genuinely needs.
        </>
      }
      footer={
        <ActionBar
          dirty={dirty}
          saving={save.isPending}
          onSave={() => save.mutate(buildPutPayload(current, selected, offerableNames))}
          onDiscard={() => setDraft(null)}
          message={
            dirty
              ? `${selectedOfferableCount} tool${selectedOfferableCount === 1 ? '' : 's'} selected`
              : undefined
          }
          className="px-[22px]"
        />
      }
    >
      {isLoading && <Skeleton className="h-48 w-full shrink-0" />}
      {!isLoading && isError && (
        <p className="text-xs text-muted-foreground">Couldn't load your tools. Try again.</p>
      )}
      {!isLoading && !isError && (
        <>
          {stale.length > 0 && (
            <div className="shrink-0 rounded-lg border border-destructive/30 bg-destructive/5 divide-y divide-destructive/20">
              {stale.map((s) => {
                const id = `t-stale-${s.name}`;
                return (
                  <div key={s.name} className="flex items-center gap-2 px-3 py-2">
                    <Checkbox id={id} checked disabled />
                    <label htmlFor={id} className="text-sm font-mono">
                      {s.name}
                    </label>
                    <Badge variant="destructive-soft" className="ml-1">
                      {STALE_REASON_LABEL[s.reason]}
                    </Badge>
                  </div>
                );
              })}
              <div className="flex justify-end px-3 py-2">
                <Button size="sm" variant="outline" onClick={removeStale} disabled={save.isPending}>
                  Remove them
                </Button>
              </div>
            </div>
          )}

          {(data?.mcpNeedsConnect?.length ?? 0) > 0 && (
            <p className="shrink-0 text-xs text-muted-foreground">
              Connect {data?.mcpNeedsConnect?.join(', ')} under Connections to pick from{' '}
              {data?.mcpNeedsConnect?.length === 1 ? 'its' : 'their'} read-only tools.
            </p>
          )}

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tools…"
              aria-label="Search tools"
              className="min-w-0 basis-full sm:min-w-[160px] sm:basis-auto sm:flex-1"
            />
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <span
                data-testid="tool-picker-count"
                className="shrink-0 text-xs text-muted-foreground tabular-nums"
              >
                {selectedOfferableCount} of {tools.length} selected
              </span>
              <Button size="sm" variant="ghost" onClick={selectAll}>
                Select all
              </Button>
              <Button size="sm" variant="ghost" onClick={deselectAll}>
                Deselect all
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {groups.length === 0 && (
              <p className="text-xs text-muted-foreground px-1 py-2">
                {normalizedSearch ? 'No matches.' : 'No tools available yet.'}
              </p>
            )}
            <div className="flex flex-col gap-4">
              {groups.map((group) => (
                <div key={group.key} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between px-1">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      {group.tools.filter((t) => current.includes(t.name)).length} of{' '}
                      {group.tools.length}
                    </span>
                  </div>
                  <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
                    {group.tools.map((t) => {
                      const id = `t-${t.name}`;
                      const checked = current.includes(t.name);
                      return (
                        <label
                          key={t.name}
                          htmlFor={id}
                          data-testid={`tool-row-${t.name}`}
                          className={cn(
                            'flex items-start gap-2 px-3 py-2 cursor-pointer',
                            checked ? 'bg-muted/40 hover:bg-muted/60' : 'hover:bg-muted/40',
                          )}
                        >
                          <Checkbox
                            id={id}
                            checked={checked}
                            onCheckedChange={() => toggle(t.name)}
                            className="mt-0.5"
                          />
                          <span className="min-w-0">
                            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <span className="text-sm font-medium">{toolDisplayName(t)}</span>
                              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground break-all">
                                {t.name}
                              </span>
                              {t.unverified && <Badge variant="outline">Not checked</Badge>}
                            </span>
                            <InlineMarkdown
                              text={t.summary ?? t.description}
                              as="span"
                              className="block text-xs text-muted-foreground line-clamp-2"
                            />
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </PickerDialogShell>
  );
}
